import { useState } from 'react';
import { toast } from 'sonner';
import { Sparkle, Plus, Trash, TrendUp, TrendDown, Minus, Lightning } from '@phosphor-icons/react';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { runToolJob } from '@/lib/toolJob';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { TOOL_SEO, toolPath } from '@/lib/toolRoutes';
import ToolLayout, { Card, Field, Input, Select, PrimaryButton, UpsellBanner } from './_ToolLayout';
import EmailCaptureCard from '@/components/EmailCaptureCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLATFORMS = [
  ['trendyol', 'Trendyol'], ['hepsiburada', 'Hepsiburada'], ['amazon_tr', 'Amazon TR'],
  ['amazon_us', 'Amazon US'], ['etsy', 'Etsy'], ['shopify', 'Shopify'], ['shopier', 'Shopier'], ['noon', 'Noon'],
];

export default function CompetitorRadarTool() {
  const { t, language } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const seo = TOOL_SEO.rakip[language] || TOOL_SEO.rakip.tr;
  useDocumentMeta({
    title: seo.title,
    description: seo.description,
    canonical: toolPath('rakip', language),
    alternates: { tr: toolPath('rakip', 'tr'), en: toolPath('rakip', 'en'), 'x-default': toolPath('rakip', 'tr') },
    ogKind: 'tool',
  });
  const [platform, setPlatform] = useState('trendyol');
  const [my, setMy] = useState({ ad: '', url: '', ciro: '', satis: '', puan: '', iptal: '', takipci: '', urun: '', fiyat: '', kargo_sure: '' });
  const [rivals, setRivals] = useState(['']);
  const [riv, setRiv] = useState({ puan: '', iptal: '', takipci: '', urun: '', yas_ay: '', fiyat: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const n = (v) => (v === '' || v === null ? null : Number(v));

  const submit = async () => {
    if (!my.ad.trim()) { toast.error(t('tools.comp.required.name')); return; }
    const urls = rivals.map((x) => x.trim()).filter(Boolean);
    if (!urls.length) { toast.error(t('tools.comp.required.rival')); return; }
    setLoading(true);
    setResult(null);
    try {
      const myData = { ad: my.ad, url: my.url, platform, ciro: n(my.ciro), satis: n(my.satis), puan: n(my.puan), iptal: n(my.iptal), takipci: n(my.takipci), urun: n(my.urun), fiyat: n(my.fiyat), kargo_sure: n(my.kargo_sure) };
      const rivalData = { urls, platform, puan: n(riv.puan), iptal: n(riv.iptal), takipci: n(riv.takipci), urun: n(riv.urun), yas_ay: n(riv.yas_ay), fiyat: n(riv.fiyat) };
      const data = await runToolJob(`${API}/tools/competitor-radar`, { my_data: myData, rival_data: rivalData }, getAuthHeaders());
      setResult(data);
      window.scrollTo({ top: document.getElementById('comp-result')?.offsetTop || 0, behavior: 'smooth' });
    } catch (e) {
      const msg = e?.response?.data?.detail || t('tools.comp.fail');
      toast.error(typeof msg === 'string' ? msg : t('tools.comp.fail'));
    } finally { setLoading(false); }
  };

  return (
    <ToolLayout
      testId="competitor-radar-tool"
      tourId="competitor"
      title={t('tools.comp.title')}
      subtitle={t('tools.comp.subtitle')}
    >
      <Card className="mb-6">
        <Field label={t('tools.comp.platform')}>
          <Select data-testid="comp-platform" value={platform} onChange={(e) => setPlatform(e.target.value)}>
            {PLATFORMS.map(([v, l]) => <option key={v} value={v} className="bg-[#0F1B36]">{l}</option>)}
          </Select>
        </Field>

        <h3 className="text-sm uppercase tracking-wider text-[#3CD9B8] mt-6 mb-3">{t('tools.comp.section.mine')}</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t('tools.comp.field.name')} required><Input data-testid="comp-my-ad" value={my.ad} onChange={(e) => setMy({ ...my, ad: e.target.value })} /></Field>
          <Field label={t('tools.comp.field.url')}><Input data-testid="comp-my-url" value={my.url} onChange={(e) => setMy({ ...my, url: e.target.value })} placeholder="https://" /></Field>
          <Field label={t('tools.comp.field.revenue')}><Input type="number" data-testid="comp-my-ciro" value={my.ciro} onChange={(e) => setMy({ ...my, ciro: e.target.value })} /></Field>
          <Field label={t('tools.comp.field.orders')}><Input type="number" data-testid="comp-my-satis" value={my.satis} onChange={(e) => setMy({ ...my, satis: e.target.value })} /></Field>
          <Field label={t('tools.comp.field.rating')}><Input type="number" step="0.1" data-testid="comp-my-puan" value={my.puan} onChange={(e) => setMy({ ...my, puan: e.target.value })} /></Field>
          <Field label={t('tools.comp.field.cancel')}><Input type="number" step="0.1" data-testid="comp-my-iptal" value={my.iptal} onChange={(e) => setMy({ ...my, iptal: e.target.value })} /></Field>
          <Field label={t('tools.comp.field.followers')}><Input type="number" data-testid="comp-my-takipci" value={my.takipci} onChange={(e) => setMy({ ...my, takipci: e.target.value })} /></Field>
          <Field label={t('tools.comp.field.products')}><Input type="number" data-testid="comp-my-urun" value={my.urun} onChange={(e) => setMy({ ...my, urun: e.target.value })} /></Field>
          <Field label={t('tools.comp.field.price')}><Input type="number" data-testid="comp-my-fiyat" value={my.fiyat} onChange={(e) => setMy({ ...my, fiyat: e.target.value })} /></Field>
          <Field label={t('tools.comp.field.shipping')}><Input type="number" data-testid="comp-my-kargo" value={my.kargo_sure} onChange={(e) => setMy({ ...my, kargo_sure: e.target.value })} /></Field>
        </div>

        <h3 className="text-sm uppercase tracking-wider text-[#3CD9B8] mt-6 mb-3">{t('tools.comp.section.rivals')}</h3>
        <Field label={t('tools.comp.field.rival.urls')} hint={t('tools.comp.field.rival.urls.hint')}>
          <div className="space-y-2">
            {rivals.map((r, i) => (
              <div key={i} className="flex gap-2">
                <Input data-testid={`comp-rival-url-${i}`} value={r} onChange={(e) => setRivals(rivals.map((x, idx) => idx === i ? e.target.value : x))} placeholder="https://..." />
                {rivals.length > 1 && <button onClick={() => setRivals(rivals.filter((_, idx) => idx !== i))} data-testid={`comp-rival-remove-${i}`} className="px-3 text-white/60"><Trash size={18} /></button>}
              </div>
            ))}
            {rivals.length < 5 && <button onClick={() => setRivals([...rivals, ''])} data-testid="comp-rival-add" className="text-sm text-[#3CD9B8] flex items-center gap-1"><Plus size={14} /> {t('tools.comp.field.rival.urls.add')}</button>}
          </div>
        </Field>
        <p className="text-xs text-white/50 -mt-2 mb-3">{t('tools.comp.hint.rivals')}</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t('tools.comp.field.rival.rating')}><Input type="number" step="0.1" data-testid="comp-riv-puan" value={riv.puan} onChange={(e) => setRiv({ ...riv, puan: e.target.value })} /></Field>
          <Field label={t('tools.comp.field.rival.cancel')}><Input type="number" step="0.1" data-testid="comp-riv-iptal" value={riv.iptal} onChange={(e) => setRiv({ ...riv, iptal: e.target.value })} /></Field>
          <Field label={t('tools.comp.field.rival.followers')}><Input type="number" data-testid="comp-riv-takipci" value={riv.takipci} onChange={(e) => setRiv({ ...riv, takipci: e.target.value })} /></Field>
          <Field label={t('tools.comp.field.rival.products')}><Input type="number" data-testid="comp-riv-urun" value={riv.urun} onChange={(e) => setRiv({ ...riv, urun: e.target.value })} /></Field>
          <Field label={t('tools.comp.field.rival.age')}><Input type="number" data-testid="comp-riv-yas" value={riv.yas_ay} onChange={(e) => setRiv({ ...riv, yas_ay: e.target.value })} /></Field>
          <Field label={t('tools.comp.field.rival.price')}><Input type="number" data-testid="comp-riv-fiyat" value={riv.fiyat} onChange={(e) => setRiv({ ...riv, fiyat: e.target.value })} /></Field>
        </div>

        <PrimaryButton onClick={submit} disabled={loading} data-testid="comp-submit">
          {loading ? <><Sparkle size={18} className="animate-pulse" /> {t('tools.geo.submitting')}</> : <><Sparkle size={18} weight="fill" /> {t('tools.comp.submit')}</>}
        </PrimaryButton>
      </Card>

      {result && <CompResult result={result} />}
    </ToolLayout>
  );
}

function statusIcon(s) {
  if (s === 'win') return <TrendUp size={16} className="text-emerald-400" />;
  if (s === 'lose') return <TrendDown size={16} className="text-red-400" />;
  return <Minus size={16} className="text-white/40" />;
}

function CompResult({ result }) {
  const { t } = useLanguage();
  return (
    <div id="comp-result" data-testid="comp-result" className="space-y-6">
      {result.ozet && (
        <Card>
          <h3 className="text-lg font-semibold mb-4">{t('tools.comp.result.summary')}</h3>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-emerald-400 uppercase tracking-wider mb-2">{t('tools.comp.result.strong')}</p>
              <ul className="space-y-1 text-sm">
                {(result.ozet.guclu_yonler || []).map((g, i) => <li key={i} className="text-white/80">+ {g}</li>)}
              </ul>
            </div>
            <div>
              <p className="text-xs text-red-400 uppercase tracking-wider mb-2">{t('tools.comp.result.weak')}</p>
              <ul className="space-y-1 text-sm">
                {(result.ozet.zayif_yonler || []).map((z, i) => <li key={i} className="text-white/80">− {z}</li>)}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {result.karsilastirma?.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold mb-4">{t('tools.comp.result.compare')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-white/50 border-b border-white/10"><th className="py-2 px-2">{t('tools.comp.result.col.criterion')}</th><th className="py-2 px-2">{t('tools.comp.result.col.mine')}</th><th className="py-2 px-2">{t('tools.comp.result.col.rival')}</th><th className="py-2 px-2">{t('tools.comp.result.col.status')}</th></tr></thead>
              <tbody>
                {result.karsilastirma.map((c, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-2 px-2 font-medium">{c.kriter}</td>
                    <td className="py-2 px-2 text-white/80">{c.bende}</td>
                    <td className="py-2 px-2 text-white/80">{c.rakipte}</td>
                    <td className="py-2 px-2">{statusIcon(c.durum)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {result.icgorular?.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold mb-4">{t('tools.comp.result.insights')}</h3>
          <div className="space-y-3">
            {result.icgorular.map((ic, i) => {
              const colors = { kritik: 'bg-red-500/5 border-red-500/20 text-red-400', uyari: 'bg-amber-500/5 border-amber-500/20 text-amber-400', firsat: 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400' };
              const c = colors[ic.seviye] || 'bg-white/[0.04] border-white/10';
              return (
                <div key={i} className={`p-3 rounded-lg border ${c}`}>
                  <div className="text-xs uppercase font-semibold mb-1">{ic.seviye}</div>
                  <div className="text-white font-medium text-sm mb-1">{ic.baslik}</div>
                  <p className="text-xs text-white/70">{ic.aciklama}</p>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {result.odevler?.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Lightning size={20} weight="fill" className="text-[#3CD9B8]" /> {t('tools.comp.result.actions')}</h3>
          <div className="space-y-2">
            {result.odevler.map((o, i) => (
              <div key={i} className="p-3 rounded-lg bg-white/[0.04] border border-white/5">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-[#3CD9B8] uppercase font-semibold">{o.oncelik}</span>
                  <span className="text-white/50">{o.sure}</span>
                </div>
                <div className="font-medium text-sm mb-1">#{o.no} {o.baslik}</div>
                <p className="text-xs text-white/70 mb-1">{o.nasil}</p>
                <p className="text-xs text-[#3CD9B8]">{t('tools.comp.result.impact')} {o.etki}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      <UpsellBanner />
      <EmailCaptureCard toolSlug="rakip-radar" resultSnapshot={result} />
    </div>
  );
}
